// frontend/client/src/components/FeedPreview.js
// THE BEST OF BOTH WORLDS: Left-Pane Editor + Right-Pane Mainstream Mockups

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { api } from '../services/apiClient';

import {
  Instagram, MessageCircle, Linkedin, Twitter,
  Heart, MessageSquare, Share2, Bookmark, Send, Eye,
  Smartphone, Monitor, Loader2, MoreHorizontal, Music, Repeat, ThumbsUp, Plus,
  Clock, Info, X
} from 'lucide-react';

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram,     color: '#E1306C' },
  { id: 'tiktok',    name: 'TikTok',    icon: MessageCircle, color: '#000000' },
  { id: 'linkedin',  name: 'LinkedIn',  icon: Linkedin,      color: '#0A66C2' },
  { id: 'twitter',   name: 'Twitter/X', icon: Twitter,       color: '#000000' },
];

const DEMO_POST = {
  caption:       "Summer collection dropping tomorrow ☀️✨\n\nThis season is all about bold colors, light fabrics, and confidence.",
  hashtags:      ['#SummerDrop', '#FashionForward', '#NewCollection'],
  imageUrl:      'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600&h=800&fit=crop',
  profileName:   'Your Brand',
  profileHandle: '@yourbrand',
  profilePhoto:  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop',
  likes: 1247, comments: 89, saves: 156, shares: 42, timeAgo: '2h',
};

const FP_CSS = `
  .fp-root { display: flex; flex-direction: column; gap: 20px; padding: 24px; font-family: var(--font-body, "Plus Jakarta Sans", sans-serif); min-height: 100%; background: var(--bg-base); }
  .fp-page-title { font-family: var(--font-display, "Syne", sans-serif); font-size: 24px; font-weight: 700; color: var(--text-main); margin: 0 0 4px; letter-spacing: -0.02em; }
  .fp-page-sub { font-size: 13px; color: var(--text-muted); margin: 0; }
  
  .fp-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }
  .fp-platform-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 12px; border: 1px solid var(--border-light, #E5E7EB); background: var(--bg-surface, #fff); font-size: 13px; font-weight: 700; color: var(--text-main); cursor: pointer; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
  .fp-platform-btn.active { border-color: var(--brand-primary, #7C3AED); background: #F5F3FF; color: var(--brand-primary, #7C3AED); box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1); }
  
  .fp-view-toggle { display: flex; gap: 4px; padding: 4px; border-radius: 12px; background: var(--bg-canvas, #F3F4F6); border: 1px solid var(--border-light, #E5E7EB); }
  .fp-view-btn { padding: 6px 12px; border-radius: 8px; border: none; background: transparent; cursor: pointer; color: var(--text-muted); font-size: 12px; font-weight: 700; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
  .fp-view-btn.active { background: var(--bg-surface, #fff); color: var(--brand-primary, #7C3AED); box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  
  .fp-main { display: grid; grid-template-columns: 400px 1fr; gap: 24px; align-items: start; }
  
  /* Left Panel: Editor */
  .fp-editor-card { background: var(--bg-surface, #fff); border: 1px solid var(--border-light, #E5E7EB); border-radius: 20px; padding: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
  .fp-label { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin: 0 0 8px; display: block; }
  .fp-textarea { width: 100%; min-height: 120px; padding: 14px; border-radius: 12px; border: 1px solid var(--border-light, #E5E7EB); background: #F9FAFB; font-size: 14px; line-height: 1.5; font-family: var(--font-body); outline: none; transition: all 0.2s; resize: vertical; box-sizing: border-box; }
  .fp-textarea:focus { border-color: var(--brand-primary, #7C3AED); background: #fff; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1); }
  
  .fp-image-upload { border: 2px dashed #D1D5DB; border-radius: 16px; padding: 32px 24px; text-align: center; cursor: pointer; transition: all 0.2s; background: #F9FAFB; display: block; overflow: hidden; position: relative; }
  .fp-image-upload:hover { border-color: var(--brand-primary, #7C3AED); background: #F5F3FF; }
  
  .fp-publish-bar { display: flex; gap: 12px; margin-top: 24px; }
  .fp-publish-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 12px; background: linear-gradient(135deg, #7C3AED 0%, #3B82F6 100%); color: white; font-size: 14px; font-weight: 800; border: none; cursor: pointer; box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25); transition: all 0.2s; }
  .fp-publish-btn:hover { opacity: 0.95; transform: translateY(-1px); }
  .fp-publish-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .fp-upload-overlay { position: absolute; inset: 0; background: rgba(124,58,237,0.08); display: flex; align-items: center; justify-content: center; border-radius: 14px; }
  .fp-secondary-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px 20px; border-radius: 12px; background: white; color: #374151; font-size: 14px; font-weight: 700; border: 1px solid #D1D5DB; cursor: pointer; transition: all 0.2s; }
  
  /* Right Panel: Polished Phone Frame */
  .fp-preview-container { display: flex; justify-content: center; padding: 20px; background: radial-gradient(circle at center, #F9FAFB 0%, #F3F4F6 100%); border-radius: 24px; border: 1px solid #E5E7EB; min-height: 700px; align-items: center; }
  
  .fp-device-mockup {
    background: #fff;
    position: relative;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
    overflow: hidden;
    margin: 0 auto;
  }
  
  .fp-device-mockup.mobile { width: 340px; height: 680px; border-radius: 40px; border: 12px solid #000; }
  .fp-device-mockup.desktop { width: 100%; max-width: 600px; height: auto; min-height: 500px; border-radius: 16px; border: 1px solid #E5E7EB; }
  .dynamic-island { position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 100px; height: 28px; background: #000; border-radius: 20px; z-index: 50; }

  .feed-content { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; color: #000; width: 100%; height: 100%; display: flex; flex-direction: column; overflow-y: auto; overflow-x: hidden; }
  .feed-content::-webkit-scrollbar { display: none; }
  
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  
  @media(max-width: 1024px) {
    .fp-main { grid-template-columns: 1fr; }
    .fp-device-mockup.mobile { width: 320px; height: 640px; }
  }
`;

export default function FeedPreview() {
  const { currentUser, userData } = useAuth();
  const { showToast } = useToast();
  const addToast = ({ message, type = 'success' } = {}) => showToast(message, type);
  const location = useLocation();

  const incomingData = location.state || {};

  const [platform, setPlatform] = useState(incomingData.platform || 'tiktok');
  const [viewMode, setViewMode] = useState('mobile');
  const [uploading, setUploading] = useState(false);

  const [post, setPost] = useState({
    ...DEMO_POST,
    caption: incomingData.caption || DEMO_POST.caption,
    // FIX: this was never reading incomingData.hashtags at all — the spread
    // of ...DEMO_POST above always won, so real generated hashtags (now sent
    // separately by ContentLab.js) were silently replaced by unrelated demo
    // ones like #SummerDrop. That's what "hashtags aren't in the right place"
    // was — the real hashtags were still inside the caption text (correct),
    // while irrelevant demo hashtags rendered separately underneath (wrong).
    hashtags: incomingData.hashtags && incomingData.hashtags.length > 0
      ? incomingData.hashtags
      : DEMO_POST.hashtags,
  });

  const isIncomingVideo = !!incomingData.video;
  const [isVideo, setIsVideo] = useState(isIncomingVideo);

  const resolveMediaUrl = () => {
    if (isIncomingVideo && incomingData.video) {
      // FIX: was incomingData.video.processedVideo?.secure_url — that field
      // doesn't exist on the Video schema (it's processedUrl, a flat string).
      // Same typo already found and fixed in useMediaLibrary.js and
      // SavedContent.js — this is the third occurrence. Without this fix,
      // Feed Preview would silently show the original unedited upload instead
      // of whatever was actually processed in Video Tools.
      return incomingData.video.processedUrl || incomingData.video.originalVideo?.secure_url || incomingData.video.secure_url;
    }
    return DEMO_POST.imageUrl;
  };
  
  const [mediaPreview, setMediaPreview] = useState(resolveMediaUrl());

  const publishMutation = useMutation({
  mutationFn: (payload) => api.post('/publishing/one-click', payload),
  onSuccess: (response) => {
    const result = response?.results?.find(r => r.status === 'success');
    addToast({ type: 'success', message: `Published to ${platform}!` });
    if (result?.permalink || result?.redirectUrl) {
      setTimeout(() => {
        window.open(result.permalink || result.redirectUrl, '_blank', 'noopener,noreferrer');
      }, 1500);
    }
  },
  onError: (err) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.details?.[0]?.error ||
      'Failed to publish.';
    console.error('[Publish Error Details]:', err?.response?.data || err);
    addToast({ type: 'error', message: msg });
  },
});

const handlePublish = () => {
  if (!currentUser?.uid) {
    addToast({ type: 'error', message: 'Please sign in.' });
    return;
  }

  const finalMediaUrl = mediaPreview;
  if (!finalMediaUrl || finalMediaUrl.startsWith('data:') || finalMediaUrl.startsWith('blob:')) {
    addToast({ type: 'error', message: 'Please upload your media first — Instagram requires a public URL.' });
    return;
  }

  if (!finalMediaUrl.startsWith('https://')) {
    addToast({ type: 'error', message: 'Media URL must be a public HTTPS URL.' });
    return;
  }

  publishMutation.mutate({
    firebaseUID: currentUser.uid,
    platforms: [platform],
    // Links this publish event back to the source Video document, so
    // DashboardOverview.js's Content Pipeline can verify "Published" as a
    // real state instead of guessing. Undefined/null if this came from a
    // manually-entered post with no attached video — that's fine, the
    // backend already treats missing videoId as null.
    videoId: incomingData.video?._id || null,
    content: [{
      caption: post.caption,
      hashtags: post.hashtags,
      mediaUrl: finalMediaUrl,
      mediaType: isVideo ? 'VIDEO' : 'IMAGE',
    }],
  });
};

// --- Real scheduling, mirrors handlePublish's validation exactly --------
const [showScheduleModal, setShowScheduleModal] = useState(false);
const [scheduleDate, setScheduleDate] = useState('');
const [scheduleTime, setScheduleTime] = useState('12:00');

const scheduleMutation = useMutation({
  mutationFn: (payload) => api.post('/scheduling/create', payload),
  onSuccess: () => {
    addToast({ type: 'success', message: `Scheduled for ${platform}!` });
    setShowScheduleModal(false);
  },
  onError: (err) => {
    const msg = err?.response?.data?.error || 'Failed to schedule.';
    addToast({ type: 'error', message: msg });
  },
});

const handleOpenSchedule = () => {
  if (!currentUser?.uid) {
    addToast({ type: 'error', message: 'Please sign in.' });
    return;
  }
  const finalMediaUrl = mediaPreview;
  if (!finalMediaUrl || finalMediaUrl.startsWith('data:') || finalMediaUrl.startsWith('blob:') || !finalMediaUrl.startsWith('https://')) {
    addToast({ type: 'error', message: 'Please upload your media first — a public HTTPS URL is required, same as publishing.' });
    return;
  }
  setShowScheduleModal(true);
};

const handleConfirmSchedule = () => {
  if (!scheduleDate || !scheduleTime) {
    addToast({ type: 'error', message: 'Pick a date and time.' });
    return;
  }
  const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();

  scheduleMutation.mutate({
    platform,
    caption: post.caption,
    mediaUrl: mediaPreview,
    mediaType: isVideo ? 'VIDEO' : 'IMAGE',
    videoId: incomingData.video?._id || null,
    scheduledFor,
  });
};

  // Upload media directly to Cloudinary via signed upload
  // so the resulting URL is public HTTPS — required by Instagram API
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileIsVideo = file.type.startsWith('video/');
    setIsVideo(fileIsVideo);
    setUploading(true);

    try {
      // Step 1: Get signature from backend
      const sigRes = await fetch('/api/media/signature', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Firebase-UID': currentUser?.uid || ''
        },
        body: JSON.stringify({ 
          resource_type: fileIsVideo ? 'video' : 'image'
        })
      });
      const sig = await sigRes.json();
      if (!sig.success) throw new Error('Failed to get upload signature');

      // Step 2: Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.apiKey);
      formData.append('timestamp', String(sig.timestamp));
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloudName}/${fileIsVideo ? 'video' : 'image'}/upload`;
      const uploadRes = await fetch(endpoint, { method: 'POST', body: formData });
      const uploadData = await uploadRes.json();

      if (!uploadRes.ok || uploadData.error) {
        throw new Error(uploadData.error?.message || 'Upload failed');
      }

      // Step 3: Use the public Cloudinary URL for preview and publish
      setMediaPreview(uploadData.secure_url);
      addToast({ type: 'success', message: 'Media uploaded successfully!' });
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const profileName = userData?.brandName    || post.profileName;
  const handle      = userData?.username     || post.profileHandle;
  const avatar      = userData?.profilePhoto || post.profilePhoto;
  const cp          = PLATFORMS.find(p => p.id === platform);

  const renderMedia = (styleOverrides = {}) => {
    if (!mediaPreview) return <div style={{ width: '100%', height: 300, background: '#E5E7EB' }} />;
    if (isVideo) {
      return <video src={mediaPreview} autoPlay loop muted playsInline style={{ width: '100%', objectFit: 'cover', ...styleOverrides }} />;
    }
    return <img src={mediaPreview} alt="Post" style={{ width: '100%', objectFit: 'cover', ...styleOverrides }} />;
  };

  const InstagramMockup = () => (
    <div className="feed-content" style={{ background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', marginTop: viewMode === 'mobile' ? 24 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={avatar} alt="avatar" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E5E7EB' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#000' }}>{profileName}</span>
        </div>
        <MoreHorizontal size={20} color="#000" />
      </div>
      
      {renderMedia({ maxHeight: viewMode === 'mobile' ? 450 : 600 })}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 8px' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <Heart size={24} color="#000" />
          <MessageCircle size={24} color="#000" style={{ transform: 'scaleX(-1)' }} />
          <Send size={24} color="#000" />
        </div>
        <Bookmark size={24} color="#000" />
      </div>

      <div style={{ padding: '0 14px 14px' }}>
        <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px', color: '#000' }}>{post.likes.toLocaleString()} likes</p>
        <p style={{ fontSize: 13, margin: 0, color: '#000', lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600, marginRight: 6 }}>{profileName}</span>
          {post.caption}
        </p>
        {post.hashtags.length > 0 && <p style={{ color: '#00376b', fontSize: 13, margin: '4px 0 0' }}>{post.hashtags.join(' ')}</p>}
        <p style={{ fontSize: 11, color: '#737373', margin: '6px 0 0', textTransform: 'uppercase' }}>{post.timeAgo} ago</p>
      </div>
    </div>
  );

  const TikTokMockup = () => (
    <div className="feed-content" style={{ background: '#000', color: '#fff', position: 'relative', height: '100%' }}>
      {renderMedia({ height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 0, opacity: 0.9 })}
      
      <div style={{ position: 'absolute', right: 12, bottom: 90, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#fff', padding: 2, position: 'relative', marginBottom: 8 }}>
           <img src={avatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
           <div style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', background: '#FE2C55', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 'bold' }}>
             <Plus size={12} color="white" />
           </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Heart size={32} color="#fff" fill="rgba(255,255,255,0.2)" />
          <span style={{ fontSize: 12, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{post.likes > 1000 ? '1.2K' : post.likes}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <MessageCircle size={32} color="#fff" fill="rgba(255,255,255,0.2)" />
          <span style={{ fontSize: 12, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{post.comments}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Bookmark size={32} color="#fff" fill="rgba(255,255,255,0.2)" />
          <span style={{ fontSize: 12, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{post.saves}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Share2 size={32} color="#fff" fill="rgba(255,255,255,0.2)" />
          <span style={{ fontSize: 12, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{post.shares}</span>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#333', border: '8px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'spin 4s linear infinite', marginTop: 8 }}>
          <Music size={14} color="white" />
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10, padding: '20px 16px 20px', background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)' }}>
        <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{profileName}</p>
        <div style={{ fontSize: 14, margin: '0 0 12px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
          {post.caption} <strong style={{color: '#fff'}}>{post.hashtags.join(' ')}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
          <Music size={14} /> <span>Original sound - {profileName}</span>
        </div>
      </div>
    </div>
  );

  const LinkedInMockup = () => (
    <div className="feed-content" style={{ background: '#E9E5DF' }}>
      <div style={{ background: '#fff', marginTop: viewMode === 'mobile' ? 30 : 0 }}>
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px' }}>
          <img src={avatar} alt="avatar" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#000' }}>{profileName}</span>
              <span style={{ color: '#666', fontSize: 14 }}>• 1st</span>
            </div>
            <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Innovative Brand Leader</p>
            <p style={{ fontSize: 12, color: '#666', margin: 0 }}>{post.timeAgo} • 🌐</p>
          </div>
        </div>
        
        <div style={{ padding: '0 16px 12px', fontSize: 14, color: '#000', lineHeight: 1.5 }}>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{post.caption}</p>
          {post.hashtags.length > 0 && <p style={{ color: '#0A66C2', margin: '8px 0 0', fontWeight: 600 }}>{post.hashtags.join(' ')}</p>}
        </div>

        {renderMedia({ maxHeight: 500 })}

        <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', fontSize: 12, color: '#666' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ background: '#0A66C2', borderRadius: '50%', padding: 2 }}><ThumbsUp size={10} color="#fff" /></div>
            <span>{post.likes}</span>
          </div>
          <span>{post.comments} comments • {post.shares} reposts</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
          {[{i:ThumbsUp, l:'Like'}, {i:MessageSquare, l:'Comment'}, {i:Repeat, l:'Repost'}, {i:Send, l:'Send'}].map((action, i) => (
            <button key={i} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyYontent: 'center', gap: 6, padding: '12px 0', background: 'transparent', border: 'none', color: '#666', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 6 }}>
              <action.i size={18} /> {action.l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const TwitterMockup = () => (
    <div className="feed-content" style={{ background: '#fff' }}>
      <div style={{ padding: '16px', marginTop: viewMode === 'mobile' ? 24 : 0 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <img src={avatar} alt="avatar" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0F1419' }}>{profileName}</span>
                <span style={{ fontSize: 15, color: '#536471' }}>{handle} · {post.timeAgo}</span>
              </div>
              <MoreHorizontal size={18} color="#536471" />
            </div>
            
            <div style={{ fontSize: 15, color: '#0F1419', lineHeight: 1.5, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {post.caption}
              {post.hashtags.length > 0 && <p style={{ color: '#1DA1F2', margin: '4px 0 0' }}>{post.hashtags.join(' ')}</p>}
            </div>

            <div style={{ marginTop: 12, borderRadius: 16, overflow: 'hidden', border: '1px solid #EFF3F4' }}>
              {renderMedia({ maxHeight: 400 })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, color: '#536471', paddingRight: 20 }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MessageCircle size={18} /> <span style={{fontSize:13}}>{post.comments}</span></div>
               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Repeat size={18} /> <span style={{fontSize:13}}>{post.shares}</span></div>
               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Heart size={18} /> <span style={{fontSize:13}}>{post.likes}</span></div>
               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Bookmark size={18} /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fp-root">
      <style>{FP_CSS}</style>
      
      <div>
        <h1 className="fp-page-title">Feed Preview</h1>
        <p className="fp-page-sub">See exactly how your content looks before publishing</p>
      </div>
      
      <div className="fp-toolbar">
        {PLATFORMS.map(p => (
          <button key={p.id} className={`fp-platform-btn ${platform === p.id ? 'active' : ''}`} onClick={() => setPlatform(p.id)}>
            <p.icon size={16} color={p.color} /> {p.name}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <div className="fp-view-toggle">
            <button className={`fp-view-btn ${viewMode === 'mobile' ? 'active' : ''}`} onClick={() => setViewMode('mobile')}><Smartphone size={14} /> Mobile</button>
            <button className={`fp-view-btn ${viewMode === 'desktop' ? 'active' : ''}`} onClick={() => setViewMode('desktop')}><Monitor size={14} /> Desktop</button>
          </div>
        </div>
      </div>

      <div className="fp-main">
        
        {/* LEFT PANEL: The Editor */}
        <div className="fp-editor-card">
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 16px' }}>Edit Your Post</p>
          
          <label className="fp-label">Caption</label>
          <textarea className="fp-textarea" style={{ marginBottom: 16 }} value={post.caption} onChange={e => setPost(p => ({ ...p, caption: e.target.value }))} placeholder="Write your caption..." />
          
          <label className="fp-label">Hashtags</label>
          <input className="fp-textarea" style={{ minHeight: 48, marginBottom: 16 }} value={post.hashtags.join(' ')} onChange={e => setPost(p => ({ ...p, hashtags: e.target.value.split(/\s+/).filter(Boolean) }))} placeholder="#hashtag1 #hashtag2" />
          
          <label className="fp-label">Media</label>
          <label className="fp-image-upload" style={{ marginBottom: 24 }}>
            <input type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            {mediaPreview ? (
              isVideo 
                ? <video src={mediaPreview} controls style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 12, objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                : <img src={mediaPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 12, objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            ) : (
              <>
                <div style={{ width: 48, height: 48, borderRadius: 16, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  <Eye size={24} color="var(--brand-primary, #7C3AED)" />
                </div>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' }}>Click to upload media</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Supports JPG, PNG, MP4</p>
              </>
            )}
          </label>
          
          <div className="fp-publish-bar">
            <button className="fp-publish-btn" onClick={handlePublish} disabled={publishMutation.isPending || uploading}>
              {publishMutation.isPending ? <><Loader2 size={16} className="spin" /> Publishing...</> : <><Send size={16} /> Publish to {cp?.name}</>}
            </button>
            <button className="fp-secondary-btn" onClick={handleOpenSchedule}><Clock size={16} /> Schedule for later</button>

            {showScheduleModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={() => setShowScheduleModal(false)}>
                <div style={{ background: 'var(--bg-surface)', width: '100%', maxWidth: 420, borderRadius: 20, padding: 24, boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={16}/> Schedule for later</h3>
                    <button onClick={() => setShowScheduleModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18}/></button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--brand-secondary)', border: '1px solid var(--brand-border)', borderRadius: 10, fontSize: 12, color: 'var(--brand-secondary-contrast, var(--brand-primary))', marginBottom: 16 }}>
                    <Info size={14} style={{ flexShrink: 0 }}/>
                    <span>Beta limitation: scheduled posts are checked once daily, so exact publish time isn't guaranteed yet — this is being improved.</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Date</label>
                      <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-medium)', fontSize: 13, boxSizing: 'border-box' }}/>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Time</label>
                      <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-medium)', fontSize: 13, boxSizing: 'border-box' }}/>
                    </div>
                  </div>

                  <button
                    onClick={handleConfirmSchedule}
                    disabled={scheduleMutation.isPending}
                    className="fp-publish-btn"
                    style={{ width: '100%' }}
                  >
                    {scheduleMutation.isPending ? <><Loader2 size={16} className="spin" /> Scheduling...</> : <><Clock size={16} /> Confirm Schedule</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Dynamic Mockup */}
        <div className="fp-preview-container">
          <div className={`fp-device-mockup ${viewMode}`}>
            {viewMode === 'mobile' && <div className="dynamic-island" />}
            
            {platform === 'tiktok'    && <TikTokMockup />}
            {platform === 'instagram' && <InstagramMockup />}
            {platform === 'linkedin'  && <LinkedInMockup />}
            {platform === 'twitter'   && <TwitterMockup />}
          </div>
        </div>

      </div>
    </div>
  );
}